import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import MDButton from 'components/MDButton';
import CrudService from 'services/cruds-service';
import { makeStyles } from '@material-ui/core/styles';
import MDTypography from 'components/MDTypography';

function money(cents, currency = 'USD') {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100);
}

function statusColor(status) {
  return ({ sent: 'info', viewed: 'info', partial: 'warning', overdue: 'error', paid: 'success', cancelled: 'default', refunded: 'default' })[status] || 'default';
}

const useStyles = makeStyles({
  customTableHead: {
    display: 'table-header-group!important', // Use the correct CSS property without quotes
  },
});

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [paying, setPaying] = useState(false);
  const [notice, setNotice] = useState('');
  const classes = useStyles();

  const loadInvoices = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const response = await CrudService.getMyInvoices({ page: 1, page_size: 50 }, signal);
      setInvoices(response?.data || []);
    } catch (failure) {
      if (failure.name !== 'CanceledError' && failure.name !== 'AbortError') {
        setError('Invoices could not be loaded. Try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadInvoices(controller.signal);
    return () => controller.abort();
  }, [loadInvoices]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'return') {
      setNotice('Payment submitted. We are confirming it with Stripe. Refresh in a moment for the updated balance.');
      loadInvoices();
    } else if (params.get('payment') === 'cancelled') {
      setNotice('Payment was not completed. Your invoice remains open.');
    }
  }, [loadInvoices]);

  const openInvoice = async (invoiceId) => {
    setSelected(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const response = await CrudService.getMyInvoice(invoiceId);
      setSelected(response?.data || null);
    } catch (failure) {
      setDetailError('This invoice could not be opened.');
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPdf = async (invoice) => {
    try {
      const blob = await CrudService.downloadMyInvoicePdf(invoice.invoice_id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `invoice-${invoice.invoice_number}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (failure) {
      setDetailError('The invoice PDF could not be downloaded.');
    }
  };

  const payBalance = async () => {
    if (!selected || Number(selected.balance_cents) <= 0) return;
    setPaying(true);
    setDetailError('');
    try {
      const response = await CrudService.createInvoiceCheckoutSession(selected.invoice_id);
      const checkoutUrl = response?.data?.checkout_url;
      if (!checkoutUrl || new URL(checkoutUrl).hostname !== 'checkout.stripe.com') {
        throw new Error('INVALID_CHECKOUT_URL');
      }
      window.location.assign(checkoutUrl);
    } catch (failure) {
      setDetailError('Secure checkout could not be started. Refresh the invoice and try again.');
      setPaying(false);
    }
  };

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box>
          <MDTypography variant="h5" fontWeight={700}>Invoices</MDTypography>
          <MDTypography variant="body2" color="text">View balances, shipment updates, and payment status.</MDTypography>
        </Box>
        <Tooltip title="Refresh invoices">
          <IconButton aria-label="Refresh invoices" onClick={() => loadInvoices()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => loadInvoices()}>Retry</Button>}>{error}</Alert>}
      {loading && <Box display="flex" justifyContent="center" py={5}><CircularProgress size={28} /></Box>}
      {!loading && !error && invoices.length === 0 && <Alert severity="info">No invoices are available for your account.</Alert>}

      {!loading && invoices.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Your invoices">
            <TableHead classes={{ root: classes.customTableHead }}><TableRow>
              <TableCell>Invoice</TableCell><TableCell>Issued</TableCell><TableCell>Due</TableCell>
              <TableCell>Status</TableCell><TableCell align="right">Total</TableCell>
              <TableCell align="right">Balance</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>{invoices.map((invoice) => (
              <TableRow key={invoice.invoice_id} hover>
                <TableCell>{invoice.invoice_number}</TableCell>
                <TableCell>{invoice.invoice_date || '—'}</TableCell>
                <TableCell>{invoice.due_date || '—'}</TableCell>
                <TableCell><Chip size="small" label={invoice.invoice_status} color={statusColor(invoice.invoice_status)} /></TableCell>
                <TableCell align="right">{money(invoice.total_amount_cents, invoice.currency_code)}</TableCell>
                <TableCell align="right">{money(invoice.balance_cents, invoice.currency_code)}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openInvoice(invoice.invoice_id)}>View</Button>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={detailLoading || Boolean(selected) || Boolean(detailError)} onClose={() => { setSelected(null); setDetailError(''); }} fullWidth maxWidth="md">
        {detailLoading ? <DialogContent><Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box></DialogContent> : selected && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Box><MDTypography variant="h5" fontWeight={700}>Invoice {selected.invoice_number}</MDTypography>
                  <MDTypography variant="body2" color="text.secondary">Issued {selected.invoice_date} · Due {selected.due_date || 'Upon receipt'}</MDTypography></Box>
                <Chip label={selected.invoice_status} color={statusColor(selected.invoice_status)} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Invoice line items">
                  <TableHead classes={{ root: classes.customTableHead }}><TableRow><TableCell>Item</TableCell><TableCell>Qty</TableCell><TableCell align="right">Unit price</TableCell><TableCell align="right">Line total</TableCell></TableRow></TableHead>
                  <TableBody>{(selected.items || []).map((item) => (
                    <TableRow key={item.invoice_item_id}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.quantity} {item.uom_name}</TableCell>
                      <TableCell align="right">{money(item.unit_price_cents, selected.currency_code)}</TableCell>
                      <TableCell align="right">{money(item.line_total_cents, selected.currency_code)}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </Box>
              <Box ml="auto" maxWidth={340} mt={2}>
                <Box display="flex" justifyContent="space-between"><MDTypography>Subtotal</MDTypography><MDTypography>{money(selected.subtotal_cents, selected.currency_code)}</MDTypography></Box>
                <Box display="flex" justifyContent="space-between"><MDTypography>Tax</MDTypography><MDTypography>{money(selected.tax_amount_cents, selected.currency_code)}</MDTypography></Box>
                <Box display="flex" justifyContent="space-between"><MDTypography>Shipping</MDTypography><MDTypography>{money(selected.shipping_cost_cents, selected.currency_code)}</MDTypography></Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between"><MDTypography fontWeight={700}>Balance due</MDTypography><MDTypography fontWeight={700}>{money(selected.balance_cents, selected.currency_code)}</MDTypography></Box>
              </Box>
              {selected.shipments?.length > 0 && <Box mt={3}><MDTypography variant="subtitle2" mb={1}>Shipments</MDTypography>
                {selected.shipments.map((shipment) => <MDTypography key={shipment.shipment_id} variant="body2">Shipment #{shipment.shipment_id}: {shipment.shipment?.status?.status_name || 'Status unavailable'}</MDTypography>)}
              </Box>}
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
              <Button startIcon={<DownloadIcon />} onClick={() => downloadPdf(selected)}>Download PDF</Button>
              <Box display="flex" gap={1}>
                <Button onClick={() => { setSelected(null); setDetailError(''); }}>Close</Button>
                {['sent', 'viewed', 'partial', 'overdue'].includes(selected.invoice_status) && Number(selected.balance_cents) > 0 && (
                  <MDButton variant="gradient" color="info" startIcon={<PaymentIcon />} onClick={payBalance} disabled={paying}>
                    {paying ? 'Opening checkout…' : `Pay ${money(selected.balance_cents, selected.currency_code)}`}
                  </MDButton>
                )}
              </Box>
            </DialogActions>
          </>
        )}
        {!detailLoading && !selected && detailError && <DialogContent><Alert severity="error">{detailError}</Alert></DialogContent>}
      </Dialog>
    </Card>
  );
}